import React, { useState, useRef, useEffect } from 'react';
import { Settings, ChevronLeft, ChevronRight } from 'lucide-react';

interface FloatingSettingsButtonProps {
  onClick: () => void;
}

export const FloatingSettingsButton: React.FC<FloatingSettingsButtonProps> = ({ onClick }) => {
  const [position, setPosition] = useState({ x: window.innerWidth - 60, y: 200 });
  const [isDragging, setIsDragging] = useState(false);
  const [isHidden, setIsHidden] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [side, setSide] = useState<'left' | 'right'>('right');
  const [shouldHide, setShouldHide] = useState(false);
  const dragStartPos = useRef({ x: 0, y: 0 });
  const hideTimerRef = useRef<NodeJS.Timeout>();
  const buttonRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging) return;

      const newX = e.clientX - dragStartPos.current.x;
      const newY = e.clientY - dragStartPos.current.y;

      const maxX = window.innerWidth - 50;
      const maxY = window.innerHeight - 50;

      setPosition({
        x: Math.max(0, Math.min(newX, maxX)),
        y: Math.max(0, Math.min(newY, maxY))
      });
    };

    const handleMouseUp = (e: MouseEvent) => {
      if (isDragging) {
        setIsDragging(false);

        const centerX = window.innerWidth / 2;
        const currentX = e.clientX;
        const targetSide = currentX < centerX ? 'left' : 'right';
        const targetX = targetSide === 'left' ? 10 : window.innerWidth - 60;

        setSide(targetSide);
        setPosition(prev => ({
          x: targetX,
          y: prev.y
        }));

        setShouldHide(true);
      }
    };

    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
      }
    };
  }, [isDragging]);

  useEffect(() => {
    if (shouldHide && !isHovered && !isDragging) {
      hideTimerRef.current = setTimeout(() => {
        setIsHidden(true);
      }, 500);
    } else {
      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
      }
      setIsHidden(false);
    }

    return () => {
      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
      }
    };
  }, [shouldHide, isHovered, isDragging]);

  // Handle window resize to keep button within bounds and stick to edge
  useEffect(() => {
    const handleResize = () => {
      const maxX = window.innerWidth - 60;
      const maxY = window.innerHeight - 60;

      setPosition(prev => {
        let newX = prev.x;
        // If currently on the right side and not moving, stick to the new right edge
        if (side === 'right' && !isDragging) {
          newX = maxX;
        }

        return {
          // Clamp within visible area
          x: Math.max(10, Math.min(newX, maxX)),
          y: Math.max(10, Math.min(prev.y, maxY))
        };
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [side, isDragging]);

  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    setIsHidden(false);
    setShouldHide(false);
    dragStartPos.current = {
      x: e.clientX - position.x,
      y: e.clientY - position.y
    };
  };

  const handleClick = (e: React.MouseEvent) => {
    if (!isDragging) {
      onClick();
    }
  };

  return (
    <div
      ref={buttonRef}
      onMouseDown={handleMouseDown}
      onClick={handleClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={`
        fixed z-50 cursor-move transition-all duration-300 ease-out
        ${isHidden && !isHovered && !isDragging ? (side === 'right' ? 'translate-x-12' : '-translate-x-12') : 'translate-x-0'}
      `}
      style={{
        left: position.x,
        top: position.y,
        opacity: isDragging ? 0.8 : 1
      }}
    >
      <div className="relative">
        <button
          className={`
            w-12 h-12 rounded-full bg-white dark:bg-slate-800 
            shadow-lg border-2 border-slate-100 dark:border-slate-700
            hover:shadow-xl hover:scale-110 active:scale-95
            transition-all duration-200 flex items-center justify-center
            ${isHidden && !isHovered && !isDragging ? 'opacity-60' : 'opacity-100'}
          `}
        >
          <Settings size={20} className="text-slate-400" />
        </button>

        {isHidden && !isHovered && !isDragging && (
          <div className={`
            absolute top-1/2 -translate-y-1/2 w-6 h-6 rounded-full 
            bg-primary-500 text-white flex items-center justify-center
            ${side === 'right' ? '-left-8' : '-right-8'}
          `}>
            {side === 'right' ? <ChevronLeft size={14} /> : <ChevronRight size={14} />}
          </div>
        )}
      </div>
    </div>
  );
};
